import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaUser, FaTruck, FaChevronLeft, FaChevronRight, FaUndo } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PlantafelService, { TeamMember, TeamAsset, LaneTeam } from '../services/PlantafelService';
import EmployeeService from '../../employees/services/EmployeeService';
import axiosInstance from '../../../config/axios';

interface WeeklyRosterModalProps {
  team: LaneTeam;
  initialWeek: string; // YYYY-MM-DD format (Monday of the week)
  onClose: () => void;
  onSaved: () => void;
}

function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatWeekString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function WeeklyRosterModal({
  team,
  initialWeek,
  onClose,
  onSaved,
}: WeeklyRosterModalProps): React.JSX.Element {
  const [currentWeek, setCurrentWeek] = useState<Date>(() => getMonday(new Date(initialWeek)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Available employees and assets (fetched once)
  const [availableEmployees, setAvailableEmployees] = useState<{ id: number; first_name: string; last_name: string }[]>([]);
  const [availableAssets, setAvailableAssets] = useState<{ id: number; name: string; license_plate?: string }[]>([]);

  // Current roster data
  const [roster, setRoster] = useState<{
    effective_from_week: string;
    is_override: boolean;
    members: TeamMember[];
    assets: TeamAsset[];
  } | null>(null);

  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<number>>(new Set());
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<number>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  const weekString = formatWeekString(currentWeek);
  const weekNumber = getISOWeekNumber(currentWeek);

  // Load available employees and assets once
  useEffect(() => {
    const loadAvailable = async () => {
      try {
        const [employees, assetsResponse] = await Promise.all([
          EmployeeService.fetchAllEmployees(),
          axiosInstance.get('/api/admin/assets/'),
        ]);
        setAvailableEmployees(
          employees
            .filter((e: any) => e.is_active !== false)
            .map((e: any) => ({
              id: e.id,
              first_name: e.first_name || '',
              last_name: e.last_name || '',
            }))
        );
        const allAssets = Array.isArray(assetsResponse.data)
          ? assetsResponse.data
          : assetsResponse.data?.assets || assetsResponse.data?.results || [];
        const activeAssets = allAssets.filter((a: any) => a.is_active !== false && a.status === 'ACTIVE');
        setAvailableAssets(
          activeAssets.map((a: any) => ({
            id: a.id,
            name: a.name || a.asset_name || '',
            license_plate: a.license_plate,
          }))
        );
      } catch (err) {
        toast.error('Fehler beim Laden der Ressourcen');
      }
    };
    loadAvailable();
  }, []);

  // Load roster for current week
  const loadRoster = useCallback(async () => {
    setLoading(true);
    try {
      const data = await PlantafelService.getWeeklyRoster(team.id, weekString);
      setRoster(data);
      setSelectedMemberIds(new Set(data.members.map(m => m.employee_id)));
      setSelectedAssetIds(new Set(data.assets.map(a => a.asset_id)));
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load roster:', err);
      toast.error('Fehler beim Laden des Rosters');
    } finally {
      setLoading(false);
    }
  }, [team.id, weekString]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const handlePreviousWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() - 7);
    setCurrentWeek(newWeek);
  };

  const handleNextWeek = () => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + 7);
    setCurrentWeek(newWeek);
  };

  const toggleMember = (employeeId: number) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
    setHasChanges(true);
  };

  const toggleAsset = (assetId: number) => {
    setSelectedAssetIds(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await PlantafelService.setWeeklyRoster(
        team.id,
        weekString,
        Array.from(selectedMemberIds),
        Array.from(selectedAssetIds)
      );
      toast.success(`Roster für KW ${weekNumber} gespeichert`);
      setHasChanges(false);
      await loadRoster();
      onSaved();
    } catch (err) {
      console.error('Failed to save roster:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToDefault = async () => {
    if (!roster?.is_override) return;

    setSaving(true);
    try {
      await PlantafelService.deleteWeeklyRosterOverride(team.id, weekString);
      toast.success(`Roster für KW ${weekNumber} zurückgesetzt`);
      await loadRoster();
    } catch (err) {
      console.error('Failed to delete override:', err);
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setSaving(false);
    }
  };

  const endOfWeek = new Date(currentWeek);
  endOfWeek.setDate(endOfWeek.getDate() + 6);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '650px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
              Wochen-Roster: {team.name}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
              {team.work_area_name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#6b7280',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Week Navigation */}
        <div
          style={{
            padding: '12px 20px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
          }}
        >
          <button
            onClick={handlePreviousWeek}
            style={{
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '10px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FaChevronLeft size={12} />
          </button>
          <div style={{ textAlign: 'center', minWidth: '220px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              KW {weekNumber} / {currentWeek.getFullYear()}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {currentWeek.toLocaleDateString('de-DE')} - {endOfWeek.toLocaleDateString('de-DE')}
            </div>
          </div>
          <button
            onClick={handleNextWeek}
            style={{
              background: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '10px',
              padding: '6px 10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FaChevronRight size={12} />
          </button>
        </div>

        {/* Status Badge */}
        {roster && (
          <div style={{ padding: '10px 20px', borderBottom: '1px solid #e5e7eb' }}>
            {roster.is_override ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  background: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Angepasst für KW {weekNumber}
                <button
                  onClick={handleRevertToDefault}
                  disabled={saving}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#92400e',
                  }}
                  title="Zurücksetzen auf vorherige Woche"
                >
                  <FaUndo size={10} />
                </button>
              </span>
            ) : (
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  background: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {roster.effective_from_week === weekString
                  ? 'Standard-Roster'
                  : `Übernommen von KW ${getISOWeekNumber(new Date(roster.effective_from_week))}`}
              </span>
            )}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Laden...
            </div>
          ) : (
            <>
              {/* Employees Section */}
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FaUser size={12} />
                  Mitarbeiter ({selectedMemberIds.size} ausgewählt)
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {availableEmployees.length === 0 ? (
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>Keine Mitarbeiter verfügbar</span>
                  ) : (
                    availableEmployees.map((emp) => {
                      const isSelected = selectedMemberIds.has(emp.id);
                      return (
                        <button
                          key={emp.id}
                          onClick={() => toggleMember(emp.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: isSelected ? '#dbeafe' : '#f3f4f6',
                            border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: isSelected ? '#1d4ed8' : '#4b5563',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          <FaUser size={10} />
                          {emp.first_name} {emp.last_name}
                          {isSelected && <FaTimes size={10} />}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Assets Section */}
              <div>
                <h3
                  style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FaTruck size={12} />
                  Assets ({selectedAssetIds.size} ausgewählt)
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {availableAssets.length === 0 ? (
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>Keine Assets verfügbar</span>
                  ) : (
                    availableAssets.map((asset) => {
                      const isSelected = selectedAssetIds.has(asset.id);
                      return (
                        <button
                          key={asset.id}
                          onClick={() => toggleAsset(asset.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            background: isSelected ? '#dcfce7' : '#f3f4f6',
                            border: isSelected ? '2px solid #22c55e' : '2px solid transparent',
                            borderRadius: '20px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: isSelected ? '#166534' : '#4b5563',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          <FaTruck size={10} />
                          {asset.name}
                          {asset.license_plate && (
                            <span style={{ fontSize: '11px', opacity: 0.7 }}>
                              ({asset.license_plate})
                            </span>
                          )}
                          {isSelected && <FaTimes size={10} />}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            Änderungen gelten ab KW {weekNumber} und werden in Folgewochen übernommen
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              Schließen
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                padding: '8px 16px',
                background: hasChanges ? '#3b82f6' : '#9ca3af',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                fontWeight: 500,
                color: 'white',
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Speichern...' : `Speichern für KW ${weekNumber}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
